import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu } from 'primereact/menu';
import { utils, write } from "xlsx";
import Papa from "papaparse";
import moment from "moment";

import { DropDownField, TextInputField, CheckboxField, AutoCompleteField } from "../../../../helpers";
import Button from "../../../../helpers/Button";
import DateFilter from "../../../../common/DateFilter";
import Loading from "../../../../common/Loading";

import { getAddFilterOptions, getAutoSearchSuggestions, getDateRange, getDateString, getFullName, getMatchedRegexPattern, normalizeLabel, round, sortArrayOfObjectsInAscendingDateOrder, useFuzzSearch } from "../../../../utils";

import { getSellerPOItemsRaw } from "../../../../services/api/sellerPOItem";
import { getMaterialItemsDataByBuyerReqItemIds } from "../../../../services/api/buyerReqItem";
import { getPOPunchItemByBuyerPOIds } from "../../../../services/api/poPunchItem";
import { getLatestSellerGRNQCPunchesBySellerPODocNos } from "../../../../services/api/sellerGRNQCPunch";
import {
    fetchSellerPOItems,
    updateFollowUpSPOItems,
    updateSearchOptions,
    updateSearchParams,
    updateIsSmartSearch,
    updateIsDocNoSearch,
    setSearchConfig,
    setIsSearchByLocked
} from "../../../../services/redux/reducers/followUp/followup";
import { raiseToast } from "../../../../services/redux/reducers/toast/toast";
import { fetchBuyerCompanies } from "../../../../services/redux/reducers/commonThunk";


const followUpSearchConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Follow Up', value: 'sellerPOItemsData' },
        ]
    },
    {
        srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",
    },
    {
        srNO: 3,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            { label: 'Seller PO Date', value: 'createdAt' },
            { label: 'GRN Date', value: 'grnDate' },
            { label: 'Delivery Due Date', value: 'deliveryDueDate' },
            { label: 'Follow Up Date', value: 'followUpDate' }
        ]
    },
    {
        srNO: 4,
        static: false,
        type: "SELECT",
        name: "branchId",
        label: "Branch",
    },
    {
        srNO: 5,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "docNo",
        label: "Doc No",
    },
    {
        srNO: 6,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "fileNo",
        label: "File No",
    },
    {
        srNO: 7,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "AUTO_COMPLETE",
        name: "kamName",
        label: "Kam Name",
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "sellerCompanyName",
        label: "Seller Company Name",
    },
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "AUTO_COMPLETE",
        name: "buyerCompanyName",
        label: "Buyer Company Name",
    }
]

const docNoSearchLabels = ["docNo", "fileNo"]


const Search = (props) => {
    const { setDataType } = props

    const [isLoading, setIsLoading] = useState(false)
    const [suggestions, setSuggestions] = useState([])

    const paginationParams = useSelector(state => state.followUp.paginationParams)
    const searchParams = useSelector(state => state.followUp.searchParams)
    const searchOptions = useSelector(state => state.followUp.searchOptions)
    const isPageLoading = useSelector(state => state.followUp.loading)
    const isDocNoSearch = useSelector(state => state.followUp.isDocNoSearch)
    const isSearchByLocked = useSelector(state => state.followUp.isSearchByLocked)
    const usersData = useSelector(state => state.userDetails)
    const companyBranches = usersData?.PMCompanyDetails?.companyBranches
    const userDetails = useSelector(state => state.userDetails)

    const searchConfig = followUpSearchConfig

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)
    
    let pmCompanyUsers = userDetails.PMCompanyDetails.companyUsers || []
    let buyerCompanies = useSelector(state => state.common.buyerCompanies)

    pmCompanyUsers = pmCompanyUsers.map(eachPerson => {
        const name = getFullName(eachPerson.firstName, eachPerson.lastName)
        return { ...eachPerson, displayName: name }
    })

    buyerCompanies = buyerCompanies.map(company => {
        const companyName = company.companyName
        return { ...company, displayName: companyName }
    })

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(setSearchConfig(followUpSearchConfig))
        dispatch(fetchBuyerCompanies())

        if (searchOptions.length === 0) {
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    useEffect(() => {
        if (['kamName', 'companyName', 'buyerCompanyName'].includes(searchParams.searchBy)) {
            autoCompleteSearch(searchParams.searchBy, searchParams.searchText)
        }
    }, [searchParams.searchBy])

    const kamFuzzSearch = useFuzzSearch(pmCompanyUsers, "displayName")
    const companyFuzzSearch = useFuzzSearch(buyerCompanies, "displayName")


    const addFilterOption = (srNO) => {
        let relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        if (relatedConfig.name === "branchId") {
            const updatedCompanyBranches = companyBranches?.map(eachCompanyBranch => ({ ...eachCompanyBranch, label: eachCompanyBranch.branchName, value: eachCompanyBranch.branchId }))
            relatedConfig = {...relatedConfig, options: updatedCompanyBranches}
        }
        dispatch(updateSearchOptions([...searchOptions, { ...relatedConfig }]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.dateType = ""
            updatedSearchParams.range = ""

            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "AUTO_COMPLETE") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if (eventValue && (eventValue.userId || eventValue.companyId)) {
            eventValue = eventValue.displayName
        }

        const updatedSearchParams = { ...searchParams, [event.target.name]: eventValue }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        if (event.target.name === "searchText" && event.target.value.length > 3) {
            const availableOptions = [...followUpSearchConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            if(!isSearchByLocked){
                updatedSearchParams.searchBy = matchedPattern
            }
        }

        setDataType(updatedSearchParams?.dataType)
        dispatch(updateSearchParams(updatedSearchParams))
        if (event.target.name === "dataType") {
            dispatch(updateFollowUpSPOItems())
        }

        if (event.target.name === "searchBy"){
            dispatch(setIsSearchByLocked(true))
        }
    }

    const handleSearchByLock = () => {
        if(isSearchByLocked && searchParams.searchText === ""){
            const updatedSearchParams = { ...searchParams, searchBy : "all" }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        else if(isSearchByLocked && searchParams.searchText !== ""){
            const availableOptions = [...followUpSearchConfig]
            const matchedPattern = getMatchedRegexPattern(searchParams.searchText, availableOptions, searchParams.searchBy)
            const updatedSearchParams = { ...searchParams, searchBy : matchedPattern }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        dispatch(setIsSearchByLocked(!isSearchByLocked))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        getDateRange(range)
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const autoCompleteSearch = (searchType, query) => {
        const suggestions = getAutoSearchSuggestions({
            searchType,
            query,
            pmCompanyUsers,
            buyerCompanies,
            kamFuzzSearch,
            companyFuzzSearch
        })

        setSuggestions(suggestions)
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const downloadDataHandler = async (dataFormat) => {
        const { dataType } = searchParams
        if (dataType === "sellerPOItemsData") {
            downloadFollowUpData(dataFormat)
        }
    }

    const downloadFollowUpData = async (dataFormat) => {
        setIsLoading(true)

        let updatedSearchParams = {...searchParams}
        if(searchParams.branchId){
            updatedSearchParams.branchIds = [searchParams.branchId]
        }

        let response = await getSellerPOItemsRaw({...searchParams, paginator: false, orderTypeIds: [1, 2]})
        setIsLoading(false)
        
        if (response.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
        } else {
            const responseData = response?.data?.data

            const buyerReqItemsIds = responseData?.map(eachResponse => eachResponse?.buyerReqItemId)
            const materialDetailsRes = await getMaterialItemsDataByBuyerReqItemIds({ buyerReqItemsIds: buyerReqItemsIds })

            if (materialDetailsRes?.failure) {
                return dispatch(raiseToast({ severity: 'error', detail: materialDetailsRes.data.error, life: 3000 }))
            }
            const materialDetails = materialDetailsRes?.data

            let buyerPOIds = responseData?.map(eachSellerPOItem => eachSellerPOItem?.buyerPOId)
            let poPunchItemsResponse = await getPOPunchItemByBuyerPOIds({ buyerPOIds })
            if (poPunchItemsResponse.failure) {
                return dispatch(raiseToast({ severity: 'warn', summary: "PO Punch Items", detail: poPunchItemsResponse.data.error, life: 3000 }))
            }

            const sellerPODocNos = responseData?.map(eachSellerPOItem => eachSellerPOItem?.docNo)
            const sellerPOFileNos = responseData?.map(eachSellerPOItem => eachSellerPOItem?.fileNo)
            const sellerGRNQCPunchRes = await getLatestSellerGRNQCPunchesBySellerPODocNos({ sellerPODocNos, sellerPOFileNos })

            if (sellerGRNQCPunchRes.failure) {
                return dispatch(raiseToast({ severity: 'error', summary: "Seller GRN QC Punches", detail: sellerGRNQCPunchRes.data.error, life: 3000 }))
            }

            const poPunchItems = poPunchItemsResponse?.data
            const sellerGRNQCPunches = sellerGRNQCPunchRes?.data

            const sellerPOItemsData = []

            for (let i = 0; i < responseData.length; i++) {
                const sellerPOItem = responseData[i]
                let counter = i + 1

                let relatedPMBranch = companyBranches?.find(eachPMBranch => eachPMBranch?.branchId === sellerPOItem.sPOpmBranchId)

                const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === sellerPOItem?.materialId)

                const relatedSellerGRNQCPunch = sellerGRNQCPunches?.find(eachSellerGRNQCPunch => eachSellerGRNQCPunch?.sellerPOId === sellerPOItem?.sellerPOId && eachSellerGRNQCPunch?.buyerReqItemId === sellerPOItem?.buyerReqItemId)

                const relatedPOPunchtems = poPunchItems?.filter(eachPOPunchItem => eachPOPunchItem?.buyerPOItemId === sellerPOItem?.buyerPOItemId)
                let getLatestPOPunch = poPunchItems?.length && sortArrayOfObjectsInAscendingDateOrder(relatedPOPunchtems, "expectedDeliveryDate")
                let deliveryDateStr = poPunchItems?.length && getDateString(getLatestPOPunch[getLatestPOPunch?.length - 1]?.expectedDeliveryDate)

                let totalItemTax = round(sellerPOItem?.itemAmountBeforeTax * sellerPOItem?.GSTRate * 0.01)

                let relatedSellerDeliveryDate = new Date(sellerPOItem.sPOcreatedAt)
                let updateDeliveryDate = relatedSellerDeliveryDate.getDate() + parseInt(sellerPOItem?.leadTime)
                relatedSellerDeliveryDate.setDate(updateDeliveryDate)

                const downloadObj = {
                    'Sr. No.': counter,
                    'UNIQUE ID': sellerPOItem.sPOdocNo,
                    'KAM NAME': getFullName(sellerPOItem.kamFirstName, sellerPOItem.kamLastName),
                    'KAM CITY': relatedPMBranch?.branchName,
                    'SELLER PO NO': sellerPOItem.sPOdocNo,
                    'SELLER PO DATE': getDateString(sellerPOItem.sPOcreatedAt),
                    'SELLER NAME': sellerPOItem.sellerCompanyName,
                    'SELLER DELIVERY DATE': getDateString(relatedSellerDeliveryDate),
                    'BUYER PO NUMBER': sellerPOItem.bPOclientPONum,
                    'BUYER PO DATE': getDateString(sellerPOItem.bPOcreatedAt),
                    'BUYER NAME': sellerPOItem.buyerCompanyName,
                    'PRODUCT NAME': relatedMaterial?.productName,
                    'PRODUCT SPEICIFICATION': relatedMaterial?.productSpecification,
                    'MATERIAL ID': sellerPOItem.materialId,
                    'SELLER UNIT PRICE': sellerPOItem.unitPrice,
                    'SELLER PO QTY': sellerPOItem.qty,
                    'ACCEPTED QTY': relatedSellerGRNQCPunch?.totalAcceptedQuantity ? relatedSellerGRNQCPunch?.totalAcceptedQuantity : 0,
                    "REJECTED QTY": relatedSellerGRNQCPunch?.totalRejectedQuantity ? relatedSellerGRNQCPunch?.totalRejectedQuantity : 0,
                    "SHORT QTY": relatedSellerGRNQCPunch?.totalShortQuantity ? relatedSellerGRNQCPunch?.totalShortQuantity : 0,
                    'ITEM AMOUNT BEFORE TAX': sellerPOItem.itemAmountBeforeTax,
                    'GST RATE': sellerPOItem.GSTRate,
                    'ITEM TAX': totalItemTax,
                    'ITEM AMOUNT WITH TAX': sellerPOItem.itemAmountWithTax,
                    'DELIVERY DUE DATE': deliveryDateStr,
                    'REMARKS': sellerPOItem.remarks,
                    'FOLLOW UP DATE': sellerPOItem.followUpDate ? getDateString(sellerPOItem.followUpDate) : '-',
                    'GRN DATE': relatedSellerGRNQCPunch?.processedDate ? getDateString(relatedSellerGRNQCPunch.processedDate) : "-",
                    'STATUS': sellerPOItem.buyerReqIsRouted ? 'Routed' : 'Not Routed'
                }

                sellerPOItemsData.push(downloadObj)
            }

            if (dataFormat === "CSV") {
                let csv = Papa.unparse(sellerPOItemsData, { download: true })
                let blob = new Blob([csv])
                let a = window.document.createElement("a")
                a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
                a.download = "followUpItems.csv"
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
            }
            if (dataFormat === "XLSX") {
                const ws = utils.json_to_sheet(sellerPOItemsData)
                const wb = utils.book_new()
                utils.book_append_sheet(wb, ws, 'Sheet1')
                const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'followUpItems.xlsx'
                a.click()
                URL.revokeObjectURL(url)
            }
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadDataHandler("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadDataHandler("CSV")
        }
    ]

    const handleSearch = () => {
       
        if (searchParams?.dateType !== "") {
            if (!searchParams.fromDate || !searchParams.toDate) {
                return dispatch(raiseToast({ severity: 'error', detail: "Please select from date and to date", life: 3000 }))
            }

            let from_date = searchParams.fromDate
            let to_date = searchParams.toDate

            const date_difference = moment(new Date(to_date)).diff(moment(new Date(from_date)), 'days')

            if (date_difference > 15 && searchParams?.dateType !== 'createdAt') {
                return dispatch(raiseToast({ severity: 'error', detail: "Please select Date range within 15 days", life: 3000 }))
            }
        }

        let errorFound = false
                
        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })

        if (searchParams.searchBy === "docNo") {
            dispatch(updateIsDocNoSearch(true))
        } else {
            if (isDocNoSearch) {
                dispatch(updateIsDocNoSearch(false))
            }
        }

        const updatedSearchParams = { ...searchParams }

        if (updatedSearchParams.searchBy !== "all") {
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        if (searchParams.searchText) {
            if (searchParams.searchBy === 'all') {
                return dispatch(raiseToast({ severity: 'error', detail: `Please select a filter!` }))
            }
        }

        if (!errorFound) {
            dispatch(updateIsSmartSearch(false))
            dispatch(fetchSellerPOItems({ ...paginationParams, ...updatedSearchParams, orderTypeIds: [1, 2], pageNo: 0 }))
            props.setSelectedSellerPOItems([])
        }
    }

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }

    const handleCheckboxSelect = (event) => {
        let updatedSearchParams = { ...searchParams, [event.target.name]: event.target.checked }
        dispatch(updateSearchParams(updatedSearchParams))
    }

    let isDocNosSearch = false 
    if(docNoSearchLabels.includes(searchParams.searchBy) && searchParams.searchText !== ""){
        isDocNosSearch = true
    }
    const searchKeys = Object.keys(searchParams)
    if(searchKeys.some(eachKey => docNoSearchLabels.includes(eachKey) && searchParams[eachKey] !== "")){
        isDocNosSearch = true
    }

    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }} onKeyDown={handleSearchKeyPress} >
            <div className="grid">
                {searchOptions.map(eachOption => {
                    if (eachOption.type === "SEARCH_TEXT") {

                        let searchByOptions = [{ label: '', value: 'all' }]
                        searchConfig.map(eachSearchBy => {
                            if (eachSearchBy.isSearchByOption) {
                                const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                if(!optionInUse){
                                    searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                }
                            }
                        })
                        
                        return (
                            <>
                                <div className="flex flex-column col-12">
                                    {['kamName', 'companyName', 'buyerCompanyName'].includes(searchParams.searchBy) ? (
                                        <AutoCompleteField
                                            value={searchParams.searchText}
                                            suggestions={suggestions}
                                            searchMethod={(e) => autoCompleteSearch(searchParams.searchBy, e.query)}
                                            field="displayName"
                                            labelText="Search Text"
                                            onChange={handleSearchOnChange}
                                            name="searchText"
                                            dropdown
                                            refName={searchTextRef}
                                        />
                                    ) : (
                                        <TextInputField
                                            name="searchText"
                                            labelText="Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    )}
                                </div>
                                {(searchParams.searchText || isSearchByLocked)  && (
                                        <>
                                            <div className="flex justify-content-end p-0 col-12 remove-opt">
                                                <i
                                                    className={`pi ${isSearchByLocked ? 'pi-lock' : 'pi-lock-open'}`}
                                                    style={{ position: 'absolute', top: '6px', cursor: 'pointer', color: 'orange' }}
                                                    onClick={() => handleSearchByLock()}
                                                ></i>
                                            </div>
                                            <div className="flex flex-column col-12">
                                                <DropDownField
                                                    name="searchBy"
                                                    labelText="Search By"
                                                    options={searchByOptions}
                                                    value={searchParams["searchBy"]}
                                                    onChange={handleSearchOnChange}
                                                />
                                            </div>
                                        </>
                                    )}
                            </>
                        )
                    }
                    if (eachOption.type === "DATE") {
                        return (
                            <>
                                <DateFilter 
                                    disabled={isDocNosSearch}
                                    eachOption={eachOption} 
                                    searchParams={searchParams} 
                                    dateOptions={["MTD", "PM", "L15", "L30"]}
                                    handleDateRangeChange={handleDateRangeChange} 
                                    handleSearchOnChange={handleSearchOnChange}
                                    removeFilterOption={removeFilterOption}
                                />
                            </>
                        )
                    }
                    if (eachOption.type === "SELECT") {
                        return (
                            <>
                                {!eachOption.static ?
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                    :
                                    null
                                }
                                <div className="flex flex-column col-12">
                                    <DropDownField name={eachOption.name} options={eachOption.options} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                </div>
                            </>
                        )
                    }
                    if (eachOption.type === "TEXT") {
                        return (
                            <>
                                {!eachOption.static &&
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                }
                                {
                                    <div className="flex flex-column col-12">
                                        <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                }
                            </>
                        )
                    }

                    if (eachOption.type === "AUTO_COMPLETE") {
                        return (
                            <>
                                {!eachOption.static &&
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                }
                                {
                                    <div className="flex flex-column col-12">
                                        <AutoCompleteField
                                            value={searchParams[eachOption.name]}
                                            suggestions={suggestions}
                                            searchMethod={(e) => autoCompleteSearch(eachOption.name, e.query)}
                                            field="displayName"
                                            labelText={eachOption.label}
                                            onChange={handleSearchOnChange}
                                            name={eachOption.name}
                                            dropdown
                                        />
                                    </div>
                                }
                            </>
                        )
                    }
                })
                }
                <div className="flex flex-column col-12">
                    <div>
                        <CheckboxField name="excludeDispatchedPOs" checked={isDocNosSearch ? false : searchParams.excludeDispatchedPOs} label="Exclude Fully Dispatched Seller POs" onChange={handleCheckboxSelect} disabled={isDocNosSearch} />
                    </div>
                </div>
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>
                <Loading visible={isLoading} />
                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{ cursor: isPageLoading ? 'not-allowed' : 'pointer' }}>
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isPageLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} permissions={[237]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Search;